/**
 * ff-order-tracker.js
 *
 * Orchestrates the full order tracking flow:
 *   form submit (ff-track-form) → fetch /apps/simufy/status → shimmer (ff-order-status) → render → reveal
 *   reset button (data-ff-track-reset) → restore form, clear fields
 *
 * ff-order-status.liquid does not load any JS — this file owns the entire lifecycle.
 */

(function () {
  "use strict";

  const FORM_ATTR = "data-ff-track-form";
  const FIELD_ATTR = "data-ff-track-field";
  const SUBMIT_ATTR = "data-ff-track-submit";
  const RESET_ATTR = "data-ff-track-reset";
  const SECTION_ATTR = "data-ff-order-section";
  const CARD_TMPL = "tracking-card";
  const ITEM_TMPL = "line-item";

  let _formShopifySection = null;

  // ── Form handling ───────────────────────────────────────────────────

  document.addEventListener("click", (e) => {
    if (e.target.closest(`[${RESET_ATTR}]`)) {
      e.preventDefault();
      handleReset();
      return;
    }

    const btn = e.target.closest(`[${SUBMIT_ATTR}]`);
    if (!btn) return;
    const form = btn.closest(`form[${FORM_ATTR}]`);
    if (!form) return;
    e.preventDefault();
    handleSubmit(form, btn);
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    const input = e.target.closest("input");
    if (!input) return;
    const form = input.closest(`form[${FORM_ATTR}]`);
    if (!form) return;
    e.preventDefault();
    advanceOrSubmit(form);
  });

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function validate(fields, form) {
    clearErrors(form);
    const msgRequired =
      form.dataset.ffMsgRequired || "Este campo es obligatorio";
    const msgEmail = form.dataset.ffMsgEmail || "Introduce un email válido";
    let valid = true;

    form.querySelectorAll(`[${FIELD_ATTR}]`).forEach((wrapper) => {
      const key = wrapper.dataset.ffTrackField;
      const input = wrapper.querySelector("input");
      if (!input) return;

      const value = fields[key] ?? "";

      if (!value) {
        setError(input, msgRequired);
        valid = false;
      } else if (key === "email" && !EMAIL_RE.test(value)) {
        setError(input, msgEmail);
        valid = false;
      }
    });

    return valid;
  }

  function setError(input, message) {
    input.setAttribute("aria-invalid", "true");
    const err = document.createElement("p");
    err.setAttribute("data-ff-field-error", "");
    err.textContent = message;
    input.before(err);
  }

  function clearErrors(form) {
    form.querySelectorAll(`[${FIELD_ATTR}]`).forEach((wrapper) => {
      wrapper.querySelector("input")?.removeAttribute("aria-invalid");
      wrapper.querySelector("[data-ff-field-error]")?.remove();
    });
  }

  function showFormError(btn, message) {
    clearFormError(btn.closest(`form[${FORM_ATTR}]`));
    const err = document.createElement("p");
    err.setAttribute("data-ff-form-error", "");
    err.textContent = message;
    btn.after(err);
  }

  function clearFormError(form) {
    form?.querySelector("[data-ff-form-error]")?.remove();
  }

  function handleSubmit(form, btn) {
    const fields = readFields(form);

    if (!validate(fields, form)) return;

    clearFormError(form);

    const section = document.querySelector(`[${SECTION_ATTR}]`);
    const content = section?.querySelector(".ff-order-status-content");

    btn.disabled = true;

    const formSection = form.closest('[id^="shopify-section-"]');

    if (section) showShimmer(section);

    fetchOrderData(fields)
      .then((data) => {
        if (section && content) render(section, content, data, formSection);
        btn.disabled = false;
      })
      .catch((err) => {
        console.error("[ff-order-tracker]", err);
        if (section) hideShimmer(section);
        btn.disabled = false;

        const msg =
          err.code === 429
            ? form.dataset.ffMsgRateLimit ||
              "Demasiadas solicitudes. Inténtalo de nuevo en un minuto."
            : err.code === "not_found"
              ? form.dataset.ffMsgNotFound ||
                "No encontramos ningún pedido con esos datos."
              : form.dataset.ffMsgError ||
                "Ha ocurrido un error. Inténtalo de nuevo.";
        showFormError(btn, msg);
      });
  }

  function advanceOrSubmit(form) {
    clearErrors(form);
    const msgRequired =
      form.dataset.ffMsgRequired || "Este campo es obligatorio";
    const msgEmail = form.dataset.ffMsgEmail || "Introduce un email válido";

    let firstInvalid = null;

    form.querySelectorAll(`[${FIELD_ATTR}] input`).forEach((input) => {
      const key = input.closest(`[${FIELD_ATTR}]`)?.dataset.ffTrackField ?? "";
      const value = input.value.trim();

      if (!value) {
        setError(input, msgRequired);
        if (!firstInvalid) firstInvalid = input;
      } else if (key === "email" && !EMAIL_RE.test(value)) {
        setError(input, msgEmail);
        if (!firstInvalid) firstInvalid = input;
      }
    });

    if (firstInvalid) {
      firstInvalid.focus();
      return;
    }

    const btn = form.querySelector(`[${SUBMIT_ATTR}]`);
    if (btn && !btn.disabled) handleSubmit(form, btn);
  }

  function readFields(form) {
    const fields = {};
    form.querySelectorAll(`[${FIELD_ATTR}]`).forEach((wrapper) => {
      const key = wrapper.dataset.ffTrackField;
      const input = wrapper.querySelector("input");
      if (input) fields[key] = input.value.trim();
    });
    return fields;
  }

  function handleReset() {
    // Restore the form's Shopify section
    if (_formShopifySection) {
      _formShopifySection.style.display = "";
      _formShopifySection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      _formShopifySection = null;
    }

    // Hide status results and clean up dynamic elements
    const section = document.querySelector(`[${SECTION_ATTR}]`);
    if (section) {
      const content = section.querySelector(".ff-order-status-content");
      content
        ?.querySelectorAll("[data-ff-dynamic]")
        .forEach((el) => el.remove());
      content?.setAttribute("hidden", "");
      section
        .closest('[id^="shopify-section-"]')
        ?.classList.remove("ff-order-status--visible");
    }

    // Re-hide reveal sections
    document.querySelectorAll("[data-ff-reveal-on-order]").forEach((el) => {
      const wrapper = el.closest('[id^="shopify-section-"]');
      if (wrapper) wrapper.style.display = "none";
    });

    // Clear inputs, errors and re-enable submit buttons in every form
    document.querySelectorAll(`form[${FORM_ATTR}]`).forEach((form) => {
      clearErrors(form);
      clearFormError(form);
      form.querySelectorAll("input").forEach((input) => {
        input.value = "";
      });
      form.querySelectorAll(`[${SUBMIT_ATTR}]`).forEach((btn) => {
        btn.disabled = false;
      });
    });
  }

  // ── Renderer ────────────────────────────────────────────────────────

  function render(section, content, data, formSection) {
    fill(content, "customer-name", data.customerName);
    fill(content, "order-number", data.orderNumber);
    fill(content, "order-date", data.orderDate);

    const deliveryContainer = content
      .querySelector('[data-ff-role="estimated-delivery"]')
      ?.closest("p");
    if (deliveryContainer) deliveryContainer.removeAttribute("hidden");
    if (data.estimatedDelivery) {
      fill(content, "estimated-delivery", data.estimatedDelivery);
    } else {
      deliveryContainer?.setAttribute("hidden", "");
    }

    const alert = content.querySelector('[data-ff-role="preorder-alert"]');
    if (alert) {
      if (data.hasPreorder) {
        alert.removeAttribute("data-ff-hidden");
      } else {
        alert.setAttribute("data-ff-hidden", "");
      }
    }

    fill(content, "shipping-address", data.shippingAddress);

    const billingEl = content.querySelector('[data-ff-role="billing-address"]');
    if (billingEl) {
      billingEl.textContent = data.billingMatchesShipping
        ? billingEl.dataset.ffBillingSame || ""
        : data.billingAddress || "";
    }

    const cardBase = content.querySelector(`[data-ff-template="${CARD_TMPL}"]`);
    if (cardBase && data.fulfillments?.length) {
      const cardParent = cardBase.parentNode;
      data.fulfillments.forEach((fulfillment) => {
        const card = cardBase.cloneNode(true);
        card.removeAttribute("data-ff-template");
        card.removeAttribute("hidden");
        card.setAttribute("data-ff-dynamic", "");
        cardParent.appendChild(card);
        populateCard(card, fulfillment);
      });
      cardBase.setAttribute("hidden", "");
    }

    hideShimmer(section);
    content.removeAttribute("hidden");
    section
      .closest('[id^="shopify-section-"]')
      ?.classList.add("ff-order-status--visible");

    if (formSection) {
      formSection.style.display = "none";
      _formShopifySection = formSection;
    }

    section.scrollIntoView({ behavior: "smooth", block: "start" });

    document.querySelectorAll("[data-ff-reveal-on-order]").forEach((el) => {
      const wrapper = el.closest('[id^="shopify-section-"]');
      if (wrapper) wrapper.style.display = "";
    });
  }

  // ── Card population ──────────────────────────────────────────────────

  function populateCard(card, fulfillment) {
    const badge = card.querySelector('[data-ff-role="shipment-status"]');
    const btn = card.querySelector('[data-ff-role="tracking-url"]');

    if (badge) {
      const badgeBlock = badge.closest('[id^="shopify-block-"]') || badge;
      if (fulfillment.status) {
        badge.textContent = fulfillment.status;
        if (fulfillment.statusKey)
          badge.dataset.ffState = String(fulfillment.statusKey);
        badgeBlock.removeAttribute("hidden");
      } else {
        badgeBlock.setAttribute("hidden", "");
      }
    }

    if (btn) {
      const btnBlock = btn.closest('[id^="shopify-block-"]') || btn;
      const trackings = (fulfillment.trackings || []).filter((tr) => tr.url);
      const hideBtn = () => {
        // If the block wrapper also contains the badge, hide only the <a> to avoid
        // collateral damage — inline style wins over class-level display.
        if (badge && btnBlock.contains(badge)) {
          btn.style.display = "none";
        } else {
          btnBlock.setAttribute("hidden", "");
        }
      };

      if (trackings.length === 0) {
        hideBtn();
      } else if (trackings.length === 1) {
        btn.setAttribute("href", trackings[0].url);
        if (fulfillment.trackingLabel) btn.textContent = fulfillment.trackingLabel;
        btnBlock.removeAttribute("hidden");
        btn.style.display = "";
      } else {
        // N>1: un botón por bulto, clonando el <a> (como se clonan las cards).
        const label = fulfillment.trackingLabelMulti || fulfillment.trackingLabel || "";
        btn.setAttribute("href", trackings[0].url);
        btn.textContent = label.replace("{n}", "1");
        btn.style.display = "";
        let anchor = btn;
        for (let i = 1; i < trackings.length; i++) {
          const clone = btn.cloneNode(true);
          clone.removeAttribute("id");
          clone.setAttribute("href", trackings[i].url);
          clone.textContent = label.replace("{n}", String(i + 1));
          anchor.after(clone);
          anchor = clone;
        }
        btnBlock.removeAttribute("hidden");
      }
    }

    const multiAlert = card.querySelector('[data-ff-role="multitracking-alert"]');
    if (multiAlert) {
      // El banner sustituye a los milestones ocultos: multi-tracking manda sobre FedEx.
      if (fulfillment.hasMultiTracking || fulfillment.isFedEx) {
        const alertText = multiAlert.querySelector(".ff-alert-order__text");
        const custom = fulfillment.hasMultiTracking
          ? multiAlert.dataset.ffTextMulti
          : multiAlert.dataset.ffTextFedex;
        if (alertText && custom) alertText.textContent = custom;
        multiAlert.removeAttribute("data-ff-hidden");
      } else {
        multiAlert.setAttribute("data-ff-hidden", "");
      }
    }

    const shippedViaEl = card.querySelector('[data-ff-role="shipped-via"]');
    if (shippedViaEl) {
      const shippedViaBlock =
        shippedViaEl.closest('[id^="shopify-block-"]') || shippedViaEl;
      if (fulfillment.shippedVia) {
        shippedViaEl.innerHTML = fulfillment.shippedVia;
        shippedViaBlock.removeAttribute("hidden");
      } else {
        shippedViaBlock.setAttribute("hidden", "");
      }
    }

    const milestonesEl = card.querySelector('[data-ff-role="milestones"]');
    if (milestonesEl) {
      if (fulfillment.milestoneStep == null) {
        milestonesEl.setAttribute("hidden", "");
      } else {
        milestonesEl.setAttribute(
          "data-active",
          String(Math.floor(fulfillment.milestoneStep)),
        );
      }
    }

    const itemBase = card.querySelector(`[data-ff-template="${ITEM_TMPL}"]`);
    if (itemBase && fulfillment.lineItems?.length) {
      const itemParent = itemBase.parentNode;
      fulfillment.lineItems.forEach((item) => {
        const el = itemBase.cloneNode(true);
        el.removeAttribute("data-ff-template");
        el.removeAttribute("hidden");
        el.setAttribute("data-ff-dynamic", "");
        itemParent.appendChild(el);
        populateLineItem(el, item);
      });
      itemBase.setAttribute("hidden", "");
    }
  }

  // ── Line-item population ─────────────────────────────────────────────

  function populateLineItem(el, item) {
    const img = el.querySelector('[data-ff-role="item-image"]');
    if (img && item.imageUrl) {
      img.src = item.imageUrl;
      img.alt = item.imageAlt || item.title || "";
    }

    fill(el, "item-title", item.title);
    fill(el, "item-properties", item.properties);

    if (item.estimatedDelivery) {
      fill(el, "item-estimated-delivery", item.estimatedDelivery);
    } else {
      const edEl = el.querySelector('[data-ff-role="item-estimated-delivery"]');
      if (edEl) edEl.closest("[class]")?.setAttribute("hidden", "");
    }

    const preEl = el.querySelector('[data-ff-role="item-preorder"]');
    if (preEl && !item.isPreorder) preEl.setAttribute("hidden", "");
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  function fill(root, role, value) {
    if (!value) return;
    const el = root.querySelector?.(`[data-ff-role="${role}"]`);
    if (el) el.textContent = value;
  }

  function showShimmer(section) {
    section
      .querySelector(".ff-order-status-shimmer")
      ?.removeAttribute("hidden");
    section
      .querySelector(".ff-order-status-content")
      ?.setAttribute("hidden", "");
    section.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function hideShimmer(section) {
    section
      .querySelector(".ff-order-status-shimmer")
      ?.setAttribute("hidden", "");
  }

  // ── Fetch ────────────────────────────────────────────────────────────

  async function fetchOrderData(fields) {
    const params = new URLSearchParams({
      email: fields["email"] ?? "",
      order: fields["order-number"] ?? "",
    });

    const res = await fetch(`/apps/simufy/status?${params}`);

    if (res.status === 429)
      throw Object.assign(new Error("rate_limited"), { code: 429 });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const json = await res.json();
    if (!json.found)
      throw Object.assign(new Error("not_found"), { code: "not_found" });

    return transform(json.order);
  }

  // ── API → internal model transform ───────────────────────────────────

  const PIPELINE_STEP = {
    ordered: 1,
    ready_to_ship: 2,
    on_delivery: 3,
    delivered: 4,
  };

  // Fallback ES. En storefront se sobreescriben con las traducidas que la section
  // inyecta como data-* (este asset estático no puede resolver el filtro `t`).
  const STRINGS_FALLBACK = {
    status: {
      ordered: "Pedido recibido",
      ready_to_ship: "Preparando envío",
      on_delivery: "En camino",
      delivered: "Entregado",
      cancelled: "Envío cancelado",
      preorder: "Pre-order",
      processing: "En proceso",
    },
    trackingLabel: "Ver tracking",
    trackingLabelMulti: "Ver tracking bulto {n}",
    shippedVia: "Enviado vía",
    toBeShippedVia: "Se enviará vía",
    trackingNumber: "Seguimiento",
    estimatedDelivery: "Envío estimado",
    noDate: "Fecha no disponible",
    locale: "es-ES",
  };

  function trackerStrings() {
    const ds = document.querySelector(`[${SECTION_ATTR}]`)?.dataset || {};
    const f = STRINGS_FALLBACK;
    return {
      status: {
        ordered: ds.ffLabelOrdered || f.status.ordered,
        ready_to_ship: ds.ffLabelReadyToShip || f.status.ready_to_ship,
        on_delivery: ds.ffLabelOnDelivery || f.status.on_delivery,
        delivered: ds.ffLabelDelivered || f.status.delivered,
        cancelled: ds.ffLabelCancelled || f.status.cancelled,
        preorder: ds.ffLabelPreorder || f.status.preorder,
        processing: ds.ffLabelProcessing || f.status.processing,
      },
      trackingLabel: ds.ffTextTracking || f.trackingLabel,
      trackingLabelMulti: ds.ffTextTrackingMulti || f.trackingLabelMulti,
      shippedVia: ds.ffTextShippedVia || f.shippedVia,
      toBeShippedVia: ds.ffTextToBeShipped || f.toBeShippedVia,
      trackingNumber: ds.ffTextTrackingNumber || f.trackingNumber,
      estimatedDelivery: ds.ffTextEstimated || f.estimatedDelivery,
      noDate: ds.ffTextNoDate || f.noDate,
      locale: ds.ffLocale || f.locale,
    };
  }

  function transform(order) {
    const t = trackerStrings();
    const hasPreorder = order.shipments.some((s) =>
      s.lineItems.some((i) => i.isPreorder),
    );
    // Un preorder sin fecha (o con fecha invalida) impide prometer una entrega
    // global: mostramos el mensaje localizado en vez de una fecha.
    const preorderNoDate = order.shipments.some((s) =>
      s.lineItems.some((i) => i.isPreorder && !i.estimatedDelivery),
    );
    const orderDelivery = formatDate(order.estimatedDelivery, t.locale);
    let estimatedDelivery = null;
    if (preorderNoDate) estimatedDelivery = t.noDate;
    else if (orderDelivery) estimatedDelivery = orderDelivery;
    else if (hasPreorder) estimatedDelivery = t.noDate;
    return {
      customerName: order.customerName || null,
      orderNumber: order.orderName,
      orderDate: formatDate(order.createdAt, t.locale),
      estimatedDelivery: estimatedDelivery,
      hasPreorder: hasPreorder,
      shippingAddress: formatAddress(order.shippingAddress),
      billingAddress: order.billingMatchesShipping
        ? null
        : formatAddress(order.billingAddress),
      billingMatchesShipping: order.billingMatchesShipping,
      fulfillments: order.shipments.map((s) => transformShipment(s, t)),
    };
  }

  function formatAddress(addr) {
    if (!addr) return null;
    return [
      addr.name,
      addr.address1,
      addr.address2,
      [addr.zip, addr.city].filter(Boolean).join(" "),
      addr.province !== addr.city ? addr.province : null,
      addr.country,
    ]
      .filter(Boolean)
      .join("\n");
  }

  // El tracking (número y URL) viene del proxy (Odoo/Emipro), no es un boundary
  // que el theme controle; se escapa antes de inyectarlo por innerHTML.
  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // Whitelist de esquema: el proxy es la fuente de trackingUrl y un javascript:
  // acabaria ejecutandose en el href de los botones de tracking. Se aplica en
  // buildTrackings para cubrir de una vez los tres botones y el enlace del numero.
  function safeUrl(url) {
    return /^https?:\/\//i.test(url || "") ? url : null;
  }

  function splitTrackingNumbers(raw) {
    if (!raw) return [];
    return String(raw)
      .split("+")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  // Odoo/Emipro concatenan varios tracking con "+" en un único string y Shopify lo
  // embebe tal cual en la URL del carrier (UPS: trackNums=A+B). Verificado en
  // staging: el "+" viaja literal, no %2B — se contempla igualmente la forma
  // encoded por si otro carrier la usara.
  function individualTrackingUrl(url, concatenated, individual) {
    if (!url) return null;
    if (url.indexOf(concatenated) !== -1) {
      return url.split(concatenated).join(individual);
    }
    const enc = encodeURIComponent(concatenated);
    if (url.indexOf(enc) !== -1) {
      return url.split(enc).join(encodeURIComponent(individual));
    }
    return url;
  }

  // Se conserva el bulto aunque su URL no pase el filtro: hasMultiTracking cuenta
  // sobre este array y el aviso de multi-envio no debe desaparecer por una URL mala.
  // Los botones ya descartan las entradas sin url al pintarse.
  function buildTrackings(concatenated, url) {
    return splitTrackingNumbers(concatenated).map((n) => ({
      number: n,
      url: safeUrl(individualTrackingUrl(url, concatenated, n)),
    }));
  }

  function transformShipment(shipment, t) {
    const pipelineStatus = shipment.pipelineStatus;
    const isCancelled = pipelineStatus === "cancelled";
    const isDelivered = pipelineStatus === "delivered";
    const isUnknown = pipelineStatus === "unknown";
    const isPreorder = shipment.lineItems.some((i) => i.isPreorder);
    const isFedEx = (shipment.carrier || "").toLowerCase().includes("fedex");
    const step = PIPELINE_STEP[pipelineStatus] ?? null;
    // Entregado: el tracking del transportista ya no aporta (suele caducar), se oculta el botón.
    const trackingUrl = isDelivered ? null : shipment.trackingUrl;
    // Odoo/Emipro pueden mandar varios tracking en un string "A+B"; se parten en bultos.
    const trackings = isDelivered
      ? []
      : buildTrackings(shipment.trackingNumber, shipment.trackingUrl);
    const hasMultiTracking = trackings.length > 1;
    const parts = [];
    if (isPreorder) {
      // Preorder aún no enviado: tiempo futuro + fecha estimada en vez de tracking.
      // Sin carrier no hay nada útil que mostrar todavía, se omite la línea.
      if (shipment.carrier) {
        let line = `${t.toBeShippedVia} ${escapeHtml(shipment.carrier)}`;
        const shipDate = formatDate(shipment.estimatedDelivery, t.locale);
        if (shipDate) line += ` - <strong>${shipDate}</strong>`;
        parts.push(line);
      }
    } else {
      if (shipment.carrier) parts.push(`${t.shippedVia} ${escapeHtml(shipment.carrier)}`);
      if (shipment.trackingNumber) {
        // El número enlaza al mismo tracking que el botón "Ver tracking".
        const numberText = escapeHtml(`${t.trackingNumber}: ${shipment.trackingNumber}`);
        const numberUrl = trackings[0]?.url;
        parts.push(
          numberUrl
            ? `<strong><a href="${escapeHtml(numberUrl)}" target="_blank" rel="noopener noreferrer">${numberText}</a></strong>`
            : `<strong>${numberText}</strong>`,
        );
      }
    }

    return {
      status: isPreorder
        ? t.status.preorder
        : isUnknown
          ? t.status.processing
          : isDelivered
            ? t.status.delivered
            : (t.status[pipelineStatus] ?? null),
      statusKey: isPreorder
        ? 1
        : isCancelled
          ? "cancelled"
          : isUnknown
            ? "processing"
            : isDelivered
              ? 3
              : step,
      trackingUrl: trackingUrl,
      trackings: trackings,
      hasMultiTracking: hasMultiTracking,
      isFedEx: isFedEx,
      trackingLabel: t.trackingLabel,
      trackingLabelMulti: t.trackingLabelMulti,
      shippedVia: parts.join(" · "),
      milestoneStep:
        isFedEx || hasMultiTracking
          ? null
          : isUnknown
            ? 0
            : step,
      lineItems: shipment.lineItems.map((i) => transformLineItem(i, t)),
    };
  }

  function transformLineItem(item, t) {
    const props = [
      item.quantity ? `×${item.quantity}` : null,
      item.variantTitle,
    ]
      .filter(Boolean)
      .join(" / ");
    const itemDate = formatDate(item.estimatedDelivery, t.locale);
    // Un preorder sin fecha muestra el mensaje; un no-preorder sin fecha se oculta.
    let itemEstimated = null;
    if (itemDate) itemEstimated = `${t.estimatedDelivery}: ${itemDate}`;
    else if (item.isPreorder) itemEstimated = `${t.estimatedDelivery}: ${t.noDate}`;
    return {
      imageUrl: item.imageUrl,
      imageAlt: item.title,
      title: item.title,
      properties: props,
      isPreorder: item.isPreorder,
      estimatedDelivery: itemEstimated,
    };
  }

  function formatDate(iso, locale) {
    // Fecha ausente o inválida: null para que cada consumidor decida (mensaje "sin
    // fecha" u ocultar). Ojo: new Date(null) es la época (válida), hay que filtrar
    // antes los falsy; y new Date('basura') da Invalid Date (getTime NaN).
    if (!iso) return null;
    var d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(locale || "es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
})();
